// src/components/MobileMenuSimple.tsx
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Home,
  Users,
  Building,
  DollarSign,
  CheckSquare,
  Settings,
  TrendingUp,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const BRAND = "#3B82F6";

export function MobileMenuSimple({
                                   isOpen,
                                   onClose,
                                 }: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!mounted || !isMobile) return null;

  const navItems = [
    { icon: Home, label: "Dashboard", path: "/" },
    { icon: CheckSquare, label: "Activity", path: "/crm_activities" },
    { icon: DollarSign, label: "Leads", path: "/deals" },
    { icon: Users, label: "Contact", path: "/contacts" },
    { icon: Building, label: "Company", path: "/companies" },
    { icon: TrendingUp, label: "Revenue", path: "/invoices" },
    { icon: Settings, label: "Account", path: "/settings" },
  ];

  const navigateTo = (path: string) => {
    onClose();
    window.location.hash = path;
  };

  const menuContent = (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      <div
        className={cn(
          "mobile-menu fixed top-0 left-0 z-[9998] h-full w-[280px] max-w-[85vw] bg-background shadow-2xl transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex flex-col h-full">
          {/* ✅ Blue brand strip — matches the header */}
          <div
            className="flex items-center justify-between px-4 h-14 shrink-0"
            style={{ backgroundColor: BRAND }}
          >
            <h2 className="text-lg font-bold text-white tracking-tight">
              neXus
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/15 text-white transition-colors"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          {/* Nav items — dark background, blue accents */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map(({ icon: Icon, label, path }) => {
              const isActive =
                window.location.hash === `#${path}` ||
                (path !== "/" &&
                  window.location.hash.startsWith(`#${path}`));

              return (
                <a
                  key={path}
                  href={`#${path}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo(path);
                  }}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors touch-manipulation cursor-pointer min-h-[44px]",
                    isActive
                      ? "bg-[#1C6CE1]/10 text-[#1C6CE1] font-medium"
                      : "text-foreground hover:bg-accent",
                  )}
                >
                  <Icon
                    size={20}
                    className={cn(
                      isActive ? "text-[#1C6CE1]" : "text-muted-foreground",
                    )}
                  />
                  <span>{label}</span>
                </a>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
            v2.0 • neXus
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(menuContent, document.body);
}